import { background, black, button, divider, lightBlue, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Box, ButtonProps, CircularProgress, Button as MuiButton, css, styled } from "@mui/material";
import { MouseEvent, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

export type TButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "warning" | "danger" | "dark";
export type TButtonSize = "lg" | "md" | "sm" | "xs" | "sm-round" | "sm-round-vertical";

export type TButtonProps = {
  variant?: TButtonVariant;
  size?: TButtonSize;
  to?: string;
  isLoading?: boolean;
} & Omit<ButtonProps, "variant" | "size">;

export const Button: FCC<TButtonProps> = ({
  variant = "primary",
  size = "md",
  fullWidth,
  children,
  isLoading: isExternalLoading,
  onClick,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const methods = useFormContext();

  const handleOnclick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;
    setLoading(true);
    await onClick(e);
    setLoading(false);
  };

  const isLoading = useMemo(() => {
    if (rest.type === "submit" && !!methods) return methods.formState.isSubmitting;
    return loading || isExternalLoading;
  }, [loading, methods, rest.type, isExternalLoading]);

  return (
    <StyledButton
      btnVariant={variant}
      btnSize={size}
      onClick={handleOnclick}
      sx={{ width: fullWidth ? "100%" : "fit-content" }}
      disabled={isLoading}
      {...rest}
    >
      {children}
      {isLoading && (
        <LoadingButton>
          <CircularProgress color="inherit" />
        </LoadingButton>
      )}
    </StyledButton>
  );
};

const options = { shouldForwardProp: (propName: string) => !["btnVariant", "btnSize"].includes(propName) };

const StyledButton = styled(MuiButton, options)<{ btnVariant: TButtonVariant; btnSize: TButtonSize }>`
  &.MuiButtonBase-root {
    line-height: 1;
    text-transform: none;
    position: relative;
    overflow: hidden;
    color: ${text.white};
    white-space: nowrap;
    cursor: pointer;
    ${({ btnVariant, btnSize }) => css`
      // variants
      ${PrimaryButtonCSS}
      ${btnVariant === "secondary" && SecondaryButtonCSS};
      ${btnVariant === "outline" && OutlineButtonCSS};
      ${btnVariant === "ghost" && GhostButtonCSS};
      ${btnVariant === "warning" && WarningButtonCSS};
      ${btnVariant === "danger" && DangerButtonCSS};
      ${btnVariant === "dark" && DarkButtonCSS};

      // sizes
      ${MdButtonCSS}
      ${btnSize === "lg" && LgButtonCSS}
    ${btnSize === "sm" && SmButtonCSS}
    ${btnSize === "sm-round" && SmRoundedButtonCSS}
    ${btnSize === "sm-round-vertical" && SmRoundedVerticalButtonCSS}
    ${btnSize === "xs" && XsButtonCSS}
    `}
  }
`;

const PrimaryButtonCSS = css`
  background: ${button.primary};
  border: 2px solid ${button.primary};
  color: ${text.white};
  :hover {
    background: ${lightBlue[99]};
    color: ${button.primary};
  }
  :focus {
    background: ${button.hover_02};
    color: ${button.primary};
  }
  :active {
    background: ${button.pressed};
    color: ${text.white};
    border: 2px solid ${button.pressed};
  }
  :disabled,
  &.Mui-disabled {
    background: ${button.lightDisable};
    border: 2px solid ${button.lightDisable};
    color: ${black[90]};
  }
`;

const SecondaryButtonCSS = css`
  background: ${background.white};
  border: 2px solid ${button.disable};
  color: ${text.secondary};
  :hover {
    background: ${divider.middle};
    color: ${text.secondary};
  }
  :focus {
    background: ${button.disable};
    color: ${text.secondary};
  }
  :active {
    background: ${button.pressed};
    border: 2px solid ${button.pressed};
    color: ${text.white};
  }
  :disabled,
  &.Mui-disabled {
    background: ${button.disable};
    border: 2px solid ${button.disable};
    color: ${text.white};
  }
`;

const OutlineButtonCSS = css`
  background: ${background.white};
  color: ${button.primary};
  border: 2px solid ${button.primary};
  :hover {
    background: ${button.hover_02};
  }
  :focus {
    background: ${button.hover_02};
  }
  :active {
    background: ${button.hover_01};
    color: ${text.white};
    border: 2px solid ${button.pressed};
  }
  :disabled,
  &.Mui-disabled {
    background: ${background.disable};
    border: 2px solid ${background.disable};
    color: ${text.tertiary};
  }
`;

const GhostButtonCSS = css`
  background-color: transparent;
  color: ${text.primary};
  border: none !important;
  :hover {
    background: ${divider.low};
    color: ${text.primary};
  }
  :focus {
    background: ${divider.middle};
    color: ${text.primary};
  }
  :active {
    background: ${divider.high};
    color: ${text.primary};
  }
  :disabled,
  &.Mui-disabled {
    background: ${button.disable};
    color: ${text.white};
  }
`;

const WarningButtonCSS = css`
  background: ${button.secondary};
  border: 2px solid ${button.secondary};
  color: ${text.white};
  :hover {
    background: ${text.white};
    color: ${button.secondary};
  }
  :focus {
    background: ${text.white};
    color: ${button.secondary};
  }
  :active {
    background: ${text.white};
    color: ${button.secondary};
  }
  :disabled,
  &.Mui-disabled {
    background: ${button.disable};
    border: 2px solid ${button.disable};
    color: ${text.white};
  }
`;

const DangerButtonCSS = css`
  background: transparent !important;
  border: 2px solid ${button.danger} !important;
  color: ${button.danger} !important;
  :disabled,
  &.Mui-disabled {
    background: ${button.disable} !important;
    border: 2px solid ${button.disable} !important;
    color: ${text.white} !important;
  }
`;

const DarkButtonCSS = css`
  background: ${black[30]};
  border: 2px solid ${black[30]};
  color: ${text.white};
  :hover {
    background: ${black[60]};
    border: 2px solid ${black[60]};
    color: ${text.white};
  }
  :focus {
    background: ${black[60]};
    border: 2px solid ${black[60]};
    color: ${text.white};
  }
  :active {
    background: ${black[10]};
    border: 2px solid ${black[10]};
    color: ${text.white};
  }
  :disabled,
  &.Mui-disabled {
    background: ${button.disable};
    border: 2px solid ${button.disable};
    color: ${text.white};
  }
`;

const LgButtonCSS = css`
  height: 48px;
  padding: 0 30px;
  border-radius: ${rounded.sm};
  ${typography.head20Semi}
  .MuiCircularProgress-root {
    width: 20px !important;
    height: 20px !important;
  }
`;

const MdButtonCSS = css`
  height: 40px;
  padding: 0 22px;
  border-radius: ${rounded.sm};
  ${typography.sub16Semi}
  .MuiCircularProgress-root {
    width: 16px !important;
    height: 16px !important;
  }
`;

const SmButtonCSS = css`
  height: 37px;
  padding: 0 14px;
  min-width: 60px;
  border-radius: ${rounded.sm};
  ${typography.body14Semi}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;
const SmRoundedButtonCSS = css`
  height: 37px;
  width: 60px;
  opacity: 1;
  border-width: 2px;
  gap: 8px;
  padding: 0 22px;
  border-radius: ${rounded.sm};
  ${typography.body14Semi}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const SmRoundedVerticalButtonCSS = css`
  height: 37px;
  width: 102px;
  opacity: 1;
  border-width: 2px;
  gap: 8px;
  padding: 0 22px;
  border-radius: ${rounded.sm};
  ${typography.body14Semi}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const XsButtonCSS = css`
  height: 25px;
  padding: 0 6px;
  border-radius: ${rounded.lg};
  ${typography.cap12Bold}
  .MuiCircularProgress-root {
    width: 12px !important;
    height: 12px !important;
  }
`;

const LoadingButton = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  background-color: inherit;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
